import { Component, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, NOT_ASKED_LABEL, ReportBlock, chartColors } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { Spectrum, spectrumOf, withCaption } from './spectrum-band';
import { STAGE_CARD_STYLES, stageInk, stagePartFill, stageRowMinWidth } from './stage-card';

/** A bar for a share of nothing still shows, so a 0% cell reads as measured rather than missing. */
const MIN_BAR_WIDTH = 2;

/** One cell: a share of the row. */
interface Cell {
  text: string;
  /** The whole of it in one sentence, which is what a screen reader is given. */
  detail: string;
  /** The same, in the parts a tooltip lays out. */
  tip: { row: string; answer: string; percent: string; count: string };
  /** Width as a share of the widest cell in the matrix, so the bars are comparable. */
  width: number;
  fill: string;
}

/** One rung, and the stage it belongs to. */
interface Rung {
  /**
   * The answer as the survey worded it. The rung's identity: `totalExcludes` names answers, the
   * columns are matched to rows by it, and it is what the hover text reports. Never rewritten.
   */
  label: string;
  /** What the column is called on the slide, which is {@link label} until somebody names it. */
  name: string;
  fill: string;
  ink: string;
  /** Set on the first rung of a stage, so the header can span the rest. */
  stage: { label: string; span: number; fill: string; ink: string; at: number } | null;
  /**
   * The stage this rung belongs to, set on every rung of it rather than only the first. `stage`
   * heads a column group; this one answers "which stage am I in", which is how the answers no stage
   * has claimed are counted.
   */
  partOf: { at: number; span: number } | null;
}

/**
 * A column of the table: a rung, or the gutter between two stages.
 *
 * The gutters are real columns rather than margins because a table cell cannot carry one, and they
 * are what lets each stage read as its own card rather than as a band of a single grid.
 */
interface Slot {
  gap: boolean;
  rung: Rung | null;
  /**
   * Where this rung sits in the scale, which is **not** where its column sits in the table: the
   * gutters are columns too, so from the second stage on the two run apart. Anything positional --
   * `rungNames` is one -- has to be keyed off this and never off the loop index.
   */
  rungAt: number;
  /** First and last column of a stage, which are where the card's corners get rounded. */
  start: boolean;
  end: boolean;
}

/** One row of the matrix. */
interface LadderRow {
  name: string;
  /** Everything except the excluded rungs, where the block asked for it. */
  total: string | null;
  cells: Cell[];
}

/**
 * Relationship ladder: a battery of questions read as rungs of one scale, grouped into stages.
 *
 * The data is a stacked distribution's -- a battery sharing an answer scale, every row totalling
 * 100% -- read as a grid instead of as bars. What that buys is the stages: eight rungs is too many
 * to hold in the head, four named groups of two is not, and the grouping is the whole reason to
 * draw this rather than stacked bars.
 *
 * **The stages are authored, not derived.** Nothing in a datamap says which rungs belong together or
 * what to call the group, so the block carries the names and the spans, and a study that ladders
 * something other than brand relationships names its own. They are set in the module panel, the
 * way answer bands are, so the card is only ever drawn -- in edit mode as well.
 */
@Component({
  selector: 'app-relationship-ladder-block',
  standalone: false,
  template: `
    <div class="ladder" [class.compact]="compact" [class.has-total]="!!totalLabel">
      @if (rows.length) {
        <!-- Never narrower than its stages need. No scroll of its own: the deck gives the column
             this width and scrolls sideways itself, so the band stays under the stages it names. -->
        <div [style.min-width.px]="minWidth || null">
        <table class="grid">
          <thead>
            @if (hasStages) {
              <tr class="stages">
                <!-- Classed although they are empty: the table lays out fixed, which reads its
                     column widths off the first row, so the widths carried by the row below are
                     ignored whenever this row exists. Without them the row labels and the leading
                     column take an equal share with every rung. -->
                <th class="row-label"></th>
                @if (totalLabel) { <th class="total"></th> }
                @for (slot of slots; track $index) {
                  @if (slot.gap) { <th class="gap"></th> }
                  @if (slot.rung?.stage; as stage) {
                    <th [attr.colspan]="stage.span" [style.background]="stage.fill"
                        [style.color]="stage.ink" class="stage-head" scope="colgroup">
                      {{ stage.label }}
                    </th>
                  }
                }
              </tr>
            }
            <tr>
              <th class="row-label" scope="col"></th>
              @if (totalLabel) {
                <th class="total" scope="col">{{ totalLabel }}</th>
              }
              @for (slot of slots; track $index) {
                @if (slot.gap) { <th class="gap"></th> }
                @if (slot.rung; as rung) {
                <!-- The deck's name for the column, with the answer as the survey worded it on
                     hover: the answer stays the rung's identity. -->
                <th class="part-head" [style.background]="rung.fill" [style.color]="rung.ink"
                    scope="col" [class.start]="slot.start" [class.end]="slot.end"
                    [attr.title]="rung.label">
                  <!-- Written in place while editing, by the rung's place in the scale (see Slot). -->
                  @if (editing) {
                    <input type="text" class="ecr-inline-input" maxlength="30"
                           [attr.aria-label]="'Name for the column asked as ' + rung.label"
                           [placeholder]="rung.label"
                           [value]="rungNameText(slot.rungAt)"
                           (input)="setRungName(slot.rungAt, $any($event.target).value)">
                  } @else {
                    {{ rung.name }}
                  }
                </th>
                }
              }
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.name) {
              <tr>
                <th class="row-label" scope="row">{{ row.name }}</th>
                @if (totalLabel) { <td class="total-cell">{{ row.total }}</td> }
                @for (slot of slots; track $index) {
                  @if (slot.gap) { <td class="gap"></td> }
                  @if (cellAt(row, slot); as cell) {
                  <td class="cell" [class.start]="slot.start" [class.end]="slot.end">
                    <!-- A bar and its figure rather than a shaded block: a row totals 100%, so the
                         lengths across a row are a composition and worth seeing as lengths. -->
                    <span class="bar" [style.width.%]="cell.width" [style.background]="cell.fill"
                          aria-hidden="true"></span>
                    <span class="figure" aria-hidden="true">{{ cell.text }}</span>
                    <!-- The tooltip is drawn, so it is not read. This is the same sentence, and it
                         carries the figure too, which is why the figure above is hidden: read
                         together they would say the percentage twice. -->
                    <span class="sr-only">{{ cell.detail }}</span>
                    <span class="tip" aria-hidden="true" [style.borderColor]="cell.fill">
                      <span class="tip-head">{{ cell.tip.row }}</span>
                      <span class="tip-body">
                        <span class="tip-dot" [style.color]="cell.fill">●</span>{{ cell.tip.answer }}:
                        <b>{{ cell.tip.percent }}</b>
                      </span>
                      <span class="tip-foot">{{ cell.tip.count }}</span>
                    </span>
                  </td>
                  }
                }
              </tr>
            }
          </tbody>
        </table>

        @if (bands.bands.length) {
          <!-- What movement across the ladder means, which is an interpretation rather than an
               answer, so it is the study's own words. -->
          <div class="spectrum">
            <!-- Steps over the row labels and the leading column, so the arrow starts where the
                 stages do rather than where the card does. Outside the bar, or it would be
                 coloured too. -->
            <span class="band-offset" [class.with-total]="!!totalLabel"></span>
            <div class="arrow"
                 [style.flexGrow]="arrowSpan"
                 [style.background]="bands.background"
                 [style.clipPath]="bands.clipPath">
              @for (band of bands.bands; track $index) {
                <span class="band" [style.flexGrow]="band.grow">
                  <!-- One caption per stage, written in place while editing. -->
                  @if (editing && bandStages.length) {
                    <input type="text" class="ecr-inline-input" maxlength="60"
                           [attr.aria-label]="'Caption under ' + bandStages[$index]"
                           [placeholder]="'Under ' + bandStages[$index]"
                           [value]="captionText($index)"
                           (input)="setCaption($index, $any($event.target).value)">
                  } @else {
                    {{ band.text }}
                  }
                </span>
              }
            </div>
            <!-- Answers no stage has claimed are not part of the spectrum, so the arrow stops
                 short of them rather than running on over columns it says nothing about. -->
            @if (tailSpan) { <span class="band-tail" [style.flexGrow]="tailSpan"></span> }
          </div>
        }
        </div>
      }

      @for (problem of problems; track problem) {
        <p class="note grey4 mb-0 mt-2">{{ problem }}</p>
      }
    </div>
  `,
  styles: [STAGE_CARD_STYLES, `
    :host { display: block; }

    /* Sized to the explainer, which faces it in the deck: the two are read one after the other and
       a jump in type between them reads as two different reports. A matrix cannot be quite as
       generous -- eight rungs, a row label and a leading column all have to fit across one card --
       so the air goes into the rows and the headers rather than the columns. */
    .ladder { font-size: 15px; }
    .ladder.compact { font-size: 11px; }


    /* The seams.
       A stage here is four table cells standing in for one card, and the hairlines between them
       have to be the card's own white -- as the explainer's are, where the gaps in its grid show
       the white panel behind. Drawn as a 1px white border on each cell rather than as
       border-spacing: spacing shows the page through, which put a grey line between every header
       and a grey rim around a card the explainer drew as one solid white shape. Two neighbours
       contribute 1px each, so the seam is the same 2px, and the body -- white cells, white borders
       -- closes up into one block. */
    .grid {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: fixed;
    }
    /* The seam between cells is the card's own paper, so rungs are separated inside a stage
       without a grid being drawn over it. Between stages the gutter does the work instead. */
    .stage-head, .part-head, .cell { border: 1px solid var(--ecr-card, #fff); }

    th, td {
      padding: 10px 5px;
      font-weight: 400;
      line-height: 1.3;
      vertical-align: middle;
    }

    /* Room for the bar to start inside the card rather than against its edge. The explainer's
       points carry the same air; a matrix cannot afford as much of it, but it can afford this. */
    .cell { padding: 12px 10px; }

    /* A line along each row, so the eye carries a brand's name across to its last figure.
       Barely there on purpose -- the cards are the structure here and a strong rule would turn
       them back into one grid. It reuses the seam the cells already carry rather than adding a
       second edge, and it stops at the gutters: crossing them would join the stage cards up,
       which is the look this is meant to leave alone. */
    tbody tr:not(:last-child) .cell { border-bottom-color: rgba(0, 0, 0, 0.07); }
    tbody tr:not(:last-child) .row-label,
    tbody tr:not(:last-child) .total-cell { border-bottom: 1px solid rgba(0, 0, 0, 0.07); }

    /* And the whole row lifts while it is being read, which is the part that really connects the
       name, the leading figure and the cells. Light enough to go unnoticed until it is wanted. */
    tbody tr:hover .cell,
    tbody tr:hover .row-label,
    tbody tr:hover .total-cell { background: rgba(0, 0, 0, 0.022); }

    /* Compact is named cell by cell rather than as a plain th/td rule: a blanket rule here outranks
       the shared sheet's own compact sizes -- two classes and an element beats two classes -- and
       quietly gave the stage and rung headers a padding the explainer's never had. */
    .ladder.compact .row-label,
    .ladder.compact .total,
    .ladder.compact .total-cell { padding: 6px 4px; }
    .ladder.compact .cell { padding: 8px 7px; }

    /* Narrow on purpose: the rungs are what the module is for, and a brand name wraps to two lines
       far more happily than eight columns of figures squeeze.

       The padding on the right is the gutter: with the seams drawn as borders there is no spacing
       left to hold the leading columns off the first stage, so they hold themselves off, by the
       same width as the gutter between two stages. */
    /* The whole lead when there is no Total column, so the stages start in the same place either
       way -- and in the same place as the explainer's first panel. */
    .row-label {
      width: var(--stage-lead);
      text-align: right;
      font-size: 0.85em;
      color: var(--ecr-ink, #1b2b33);
      line-height: 1.3;
      padding-right: 12px;
    }
    .ladder.compact .row-label { font-size: 0.8em; padding-right: 7px; }

    .total, .total-cell {
      width: 9%;
      padding-left: 2px;
      padding-right: 12px;
      text-align: center;
      font-weight: 700;
      color: var(--ecr-ink, #1b2b33);
    }
    .ladder.compact .total, .ladder.compact .total-cell { padding-right: 7px; }
    .ladder.has-total .row-label { width: var(--stage-lead-label); }

    .total,
    .total-cell { width: calc(var(--stage-lead) - var(--stage-lead-label)); }
    .total { font-size: 0.85em; font-weight: 600; }
    .total-cell { font-size: 1.05em; }

    /* Bar then figure on one line: the bar carries the comparison, the figure carries the value,
       and a cell too narrow for both would lose the figure, which is the half nobody can estimate. */
    .cell { white-space: nowrap; position: relative; }

    /* The hover, shaped like the tooltip every charted module in this deck already shows -- a small
       white card, a hairline in the point's own colour, the series above and the figure below with
       a dot beside it. A native title attribute would have been free, but it appears after a delay,
       in the operating system's font, and looks nothing like the rest of the deck.

       Drawn rather than announced: the sentence beside it in .sr-only is what is read. */
    .tip {
      position: absolute;
      left: 50%;
      bottom: calc(100% - 6px);
      z-index: 5;
      transform: translateX(-50%);
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.1s ease-out;
      padding: 7px 10px;
      border: 1px solid var(--nav-grey2, #ddd);
      border-radius: 3px;
      background: var(--nav-true-white, #fff);
      box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
      text-align: left;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.5;
      color: var(--ecr-ink, #1b2b33);
      pointer-events: none;
    }
    .cell:hover .tip { visibility: visible; opacity: 1; }
    .tip-head, .tip-body, .tip-foot { display: block; }
    .tip-head { font-size: 11px; font-weight: 700; }
    .tip-dot { margin-right: 5px; font-size: 14px; line-height: 1; }
    .tip-foot { font-size: 11px; color: var(--nav-grey4, #888); }
    td .bar {
      display: inline-block;
      height: 14px;
      min-width: 2px;
      border-radius: 3px;
      vertical-align: middle;
      /* The figure is the half nobody can estimate from the bar, so it keeps its line and the bar
         gives way. A cell at 94% of the widest would otherwise push "94%" onto a second line and
         make that row taller than the rest. */
      max-width: calc(100% - 42px);
    }
    .ladder.compact td .bar { max-width: calc(100% - 32px); }
    .ladder.compact td .bar { height: 11px; }
    td .figure {
      font-size: 0.9em;
      color: var(--ecr-ink, #1b2b33);
      margin-left: 6px;
      vertical-align: middle;
      white-space: nowrap;
    }

    /* Each stage is a white card, as the explainer's panels are: the coloured headers sit on top of
       it and the figures below, with a gutter column between stages doing the job a margin would if
       a table cell could carry one. */
    /* White: a stage is a card, and this is its paper. What separates one stage from the next is
       the gutter between them, which is the block's own wash showing through. */
    .cell { background: var(--ecr-card, #fff); }
    .gap {
      width: 12px;
      padding: 0;
      background: transparent;
    }
    .ladder.compact .gap { width: 7px; }

    /* The corners.
       A table cannot round a card, only the cells at its corners, so the four are named separately:
       whichever header is topmost rounds the top, and the last row of figures rounds the bottom.
       Without this the ladder's stages were square-topped against the explainer's rounded panels --
       the same design on paper, visibly two designs on screen. */
    .stage-head {
      border-top-left-radius: var(--stage-radius);
      border-top-right-radius: var(--stage-radius);
    }
    /* No stages written, so the rungs are the top of the card. */
    thead tr:first-child .part-head.start { border-top-left-radius: var(--stage-radius); }
    thead tr:first-child .part-head.end { border-top-right-radius: var(--stage-radius); }
    tbody tr:last-child .cell.start { border-bottom-left-radius: var(--stage-radius); }
    tbody tr:last-child .cell.end { border-bottom-right-radius: var(--stage-radius); }

    /* Where the arrow starts.
       The band names the stages, so it has to begin where they do -- past the row labels and the
       leading column, which are table columns the band is not in. Without this it started at the
       card's edge and every caption sat a column to the left of the stage it was naming. */
    .band-offset { flex: 0 0 15%; }
    .band-offset.with-total { flex-basis: 24%; }
    /* And where it stops: the columns past the last stage, which the arrow does not cover. */
    .band-tail { flex-basis: 0; }

    .note { font-size: 0.85em; line-height: 1.35; }
  `],
})
export class RelationshipLadderBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;
  /**
   * Declared because the host hands every block these two. The matrix uses neither: all of it is
   * set in the module panel, so the card is drawn the same in edit mode as out of it.
   */
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  rungs: Rung[] = [];
  slots: Slot[] = [];
  rows: LadderRow[] = [];
  spectrum: string[] = [];
  bands: Spectrum = { background: 'none', clipPath: 'none', bands: [] };
  /** How many rungs each band sits over, so it lines up with the stage it names. */
  bandWeights: number[] = [];
  /** The stage each band sits under, for its input's name while editing. Empty without stages. */
  bandStages: string[] = [];
  problems: string[] = [];
  totalLabel = '';
  hasStages = false;

  private stageFills: string[] = [];

  ngOnChanges() {
    this.build();
  }

  /** A column's name exactly as stored, so clearing one does not type the answer back in. */
  rungNameText(rungAt: number): string {
    return this.block.reportType === 'stage-matrix'
      ? this.block.reportExtraData.rungNames?.[rungAt] ?? ''
      : '';
  }

  /** Names a column, padding the list to reach it. Trailing blanks say nothing, so they go. */
  setRungName(rungAt: number, name: string) {
    if (this.block.reportType !== 'stage-matrix') return;
    const stored = this.block.reportExtraData.rungNames ?? [];
    const names = Array.from({ length: Math.max(stored.length, rungAt + 1) }, (_, at) => stored[at] ?? '');
    names[rungAt] = name;
    while (names.length && !names[names.length - 1]) names.pop();
    this.onPatch?.({ rungNames: names });
  }

  captionText(index: number): string {
    return this.block.reportType === 'stage-matrix'
      ? this.block.reportExtraData.spectrum?.[index] ?? ''
      : '';
  }

  /** The caption under one stage. Positional, so patched whole; trailing blanks are not kept. */
  setCaption(index: number, caption: string) {
    if (this.block.reportType !== 'stage-matrix') return;
    this.onPatch?.({ spectrum: withCaption(this.block.reportExtraData.spectrum, index, caption) });
  }

  private build() {
    this.rungs = [];
    this.slots = [];
    this.rows = [];
    this.spectrum = [];
    this.bands = { background: 'none', clipPath: 'none', bands: [] };
    this.bandWeights = [];
    this.bandStages = [];
    this.problems = [];
    this.totalLabel = '';
    this.hasStages = false;
    if (this.block.reportType !== 'stage-matrix') return;

    const extra = this.block.reportExtraData;
    const usable = this.series.filter(s => s.known);
    for (const s of this.series) {
      if (!s.known) this.problems.push(`No data for ${s.name}.`);
    }
    if (!usable.length) return;

    const labels = rungLabels(usable);
    if (!labels.length) {
      this.problems.push('These questions have no answers in common, so there are no rungs to draw. '
        + 'A stage matrix needs one question per row, all sharing the same answer list.');
      return;
    }

    this.rungs = this.rungsFor(labels, extra.stages ?? []);
    this.slots = slotsFor(this.rungs);
    this.hasStages = this.rungs.some(rung => rung.stage);
    this.totalLabel = extra.totalLabel?.trim() ?? '';
    // One band per *drawn* stage, not per caption: a ladder whose fourth stage had no rungs left
    // would otherwise show four bands under three stages, and the band would stop meaning the stage
    // above it. Extra captions are ignored; a stage with none gets an empty band rather than losing
    // its place in the row.
    const drawn = this.rungs.map(rung => rung.stage).filter(stage => !!stage);
    const captions = (extra.spectrum ?? []).map(band => band.trim());
    // While editing, a band under every stage whether or not anything is written yet, since the
    // band is written on it: an empty one is somewhere to type, and is still not drawn once read.
    const written = captions.some(band => !!band) || (!!this.editing && drawn.length > 0);
    this.bandStages = this.editing ? drawn.map(stage => stage!.label) : [];
    this.spectrum = !written
      // Nothing written is no band at all, rather than a row of empty arrows.
      ? []
      : drawn.length
        ? drawn.map((stage, index) => captions[index] ?? '')
        : captions.filter(band => !!band);
    this.bandWeights = drawn.length ? drawn.map(stage => stage!.span) : [];
    this.bands = spectrumOf(this.spectrum, index => this.bandFill(index), this.bandWeights);

    // One scale across every cell, so a bar means the same thing wherever it is. Against the widest
    // cell rather than 100%, because a ladder whose busiest rung is 43% would otherwise draw every
    // bar in the left half of its column and waste the comparison.
    const peak = Math.max(1, ...usable.flatMap(s =>
      real(s).filter(point => labels.includes(point.label)).map(point => point.percent)));

    const excluded = new Set(extra.totalExcludes ?? []);
    this.rows = usable.map(s => this.rowFor(s, labels, peak, excluded));

    const short = usable.filter(s => !totals100(s));
    if (short.length) {
      this.problems.push(`${short.map(s => s.name).join(', ')} `
        + `${short.length === 1 ? 'does' : 'do'} not total 100%, so the rungs are not one scale. `
        + 'A stage matrix needs a single-choice question per row.');
    }
  }

  /** The rungs, coloured by the stage they fall in and carrying its header on the first of them. */
  private rungsFor(labels: string[], stages: { label: string; rungs: number }[]): Rung[] {
    this.stageFills = chartColors(this.palette, Math.max(1, stages.length || 1));

    const names = this.rungNames();
    const rungs: Rung[] = labels.map((label, index) => ({
      label,
      name: names[index]?.trim() || label,
      fill: this.stageFills[0],
      ink: stageInk(this.stageFills[0]),
      stage: null,
      partOf: null,
    }));
    let at = 0;
    stages.forEach((stage, index) => {
      const span = Math.max(0, Math.trunc(stage.rungs));
      // A stage the rungs ran out before is simply not drawn, and says nothing about it: the stages
      // are edited against whatever question the block is pointed at, so a seeded fourth stage not
      // fitting a six-rung scale is the normal state of a block somebody is still setting up.
      if (!span || at >= rungs.length) return;
      const covered = Math.min(span, rungs.length - at);
      const fill = this.stageFills[index % this.stageFills.length];

      rungs[at].stage = { label: stage.label, span: covered, fill, ink: stageInk(fill), at: index };
      for (let step = 0; step < covered; step++) {
        // Each rung after the first is lifted toward white, so a stage reads as one colour family
        // and the order within it is visible too.
        const shade = stagePartFill(fill, step);
        rungs[at + step].fill = shade;
        rungs[at + step].ink = stageInk(shade);
        rungs[at + step].partOf = { at: index, span: covered };
      }
      at += covered;
    });

    // Rungs past the last stage keep a neutral fill rather than borrowing the last stage's colour,
    // which would read as though they belonged to it.
    for (let rest = at; rest < rungs.length; rest++) {
      rungs[rest].fill = '#999999';
      rungs[rest].ink = stageInk('#999999');
    }

    return rungs;
  }

  private rowFor(s: ChartSeries, labels: string[], peak: number, excluded: Set<string>): LadderRow {
    const byLabel = new Map(real(s).map(point => [point.label, point.percent]));
    const byCount = new Map(real(s).map(point => [point.label, point.value]));

    const counted = labels
      .filter(label => !excluded.has(label))
      .reduce((sum, label) => sum + (byLabel.get(label) ?? 0), 0);

    return {
      name: s.name,
      total: this.totalLabel ? `${Math.round(counted)}%` : null,
      cells: labels.map((label, index) => {
        const value = byLabel.get(label) ?? 0;
        const count = byCount.get(label) ?? 0;
        const percent = `${Math.round(value)}%`;
        return {
          text: percent,
          // The column header is the deck's wording, so the answer as asked has to be readable
          // somewhere -- along with the count, which a percentage of an unstated base hides.
          detail: `${s.name} — ${label}: ${percent} (${Math.round(count)} of ${s.base})`,
          tip: {
            row: s.name,
            answer: label,
            percent,
            count: `${Math.round(count)} of ${s.base}`,
          },
          width: Math.max(MIN_BAR_WIDTH, (value / peak) * 100),
          fill: this.rungs[index]?.fill ?? this.stageFills[0],
        };
      }),
    };
  }

  /** The width the table needs -- see ladderMinWidth, which the deck asks the same question of. */
  get minWidth(): number {
    return this.rows.length ? ladderMinWidth(this.block, this.series, !!this.compact) : 0;
  }

  /**
   * How wide the arrow runs, and what is left after it, in rungs.
   *
   * The band names the stages, so it has to stop where they do: with two answers ungrouped at the
   * end, an arrow reaching the edge of the card would point at columns it says nothing about. With
   * no stages at all there is nothing to line up with, so it spans the lot, as it did before.
   */
  get arrowSpan(): number {
    return this.hasStages ? this.rungs.length - this.unallocated : 1;
  }

  get tailSpan(): number {
    return this.hasStages ? this.unallocated : 0;
  }

  /**
   * How many answers no stage has claimed. The stages divide one fixed scale left to right, so this
   * is what is left at the end of it, which the spectrum's arrow stops short of.
   */
  get unallocated(): number {
    return this.rungs.filter(rung => !rung.partOf).length;
  }

  /** The deck's names for the columns, left to right. Blank or missing falls back to the answer. */
  private rungNames(): string[] {
    return this.block.reportType === 'stage-matrix'
      ? this.block.reportExtraData.rungNames ?? []
      : [];
  }

  /** The figure a slot shows, found by where its rung sits in the ladder. */
  cellAt(row: LadderRow, slot: Slot): Cell | null {
    if (!slot.rung) return null;
    return row.cells[this.rungs.indexOf(slot.rung)] ?? null;
  }

  /** A spectrum band takes its stage's colour where there is one, so the two read as one scheme. */
  bandFill(index: number): string {
    return this.stageFills[index % Math.max(1, this.stageFills.length)] ?? 'var(--nav-grey3, #999)';
  }

}

/**
 * The width a matrix needs to draw its narrowest stage at its minimum, worked out from the block and
 * its series alone -- the stages as `rungsFor` draws them, cut short where the rungs run out -- so
 * the deck can widen the module's column before the module exists. None in a compare column
 * (`compact`), where every column shares the width and there is no room to give one of them more.
 */
export function ladderMinWidth(block: PageSection, series: readonly ChartSeries[], compact: boolean): number {
  if (compact || block.reportType !== 'stage-matrix') return 0;
  const rungs = rungLabels(series.filter(s => s.known)).length;
  if (!rungs) return 0;
  const spans: number[] = [];
  let at = 0;
  for (const stage of block.reportExtraData.stages ?? []) {
    const span = Math.max(0, Math.trunc(stage.rungs));
    if (!span || at >= rungs) continue;
    const covered = Math.min(span, rungs - at);
    spans.push(covered);
    at += covered;
  }
  return stageRowMinWidth(spans, rungs - at, 12);
}

/**
 * The columns to draw: the rungs, with a gutter before each stage after the first.
 *
 * The gutter is what separates one stage's card from the next. Rungs past the last stage carry no
 * card of their own, so they take no gutter either -- they are the leftovers, and framing them would
 * say they were a stage.
 */
function slotsFor(rungs: readonly Rung[]): Slot[] {
  const slots: Slot[] = [];
  rungs.forEach((rung, index) => {
    if (rung.stage && index > 0) {
      slots.push({ gap: true, rung: null, rungAt: -1, start: false, end: false });
    }
    slots.push({
      gap: false,
      rung,
      rungAt: index,
      start: !!rung.stage,
      // The last column of a stage is the one before the next stage starts, or the last of all.
      end: !!rungs[index + 1]?.stage || index === rungs.length - 1,
    });
  });
  return slots;
}

/** Real answers only: "Not asked" is a gap in a compare column, not a rung. */
function real(series: ChartSeries) {
  return series.points.filter(point => point.optionIndex >= 0 && point.label !== NOT_ASKED_LABEL);
}

/**
 * The rungs, in the order the scale was written.
 *
 * A union across rows rather than the first row's answers: `aggregate()` drops an option nobody
 * chose, so a rung nobody sat on for the first brand would otherwise disappear from the ladder and
 * shift every stage's span by one.
 */
export function rungLabels(series: readonly ChartSeries[]): string[] {
  const at = new Map<string, number>();
  for (const s of series) {
    for (const point of real(s)) {
      const seen = at.get(point.label);
      if (seen === undefined || point.optionIndex < seen) at.set(point.label, point.optionIndex);
    }
  }
  // Tie-broken on the label so the ladder is the same twice running: two rows whose scales genuinely
  // disagree would otherwise order by whichever the Map happened to see first.
  return [...at.entries()]
    .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
    .map(([label]) => label);
}

/**
 * Whether a row's rungs account for everyone who answered.
 *
 * The ladder rests on each respondent sitting on exactly one rung, which is what lets two rows be
 * compared -- on a multi-punch question the percentages are of overlapping groups and a row that
 * "adds to 140%" is not a ladder at all. A point of slack for rounding, as elsewhere.
 */
function totals100(series: ChartSeries): boolean {
  if (series.multiResponse) return false;
  const total = series.points.reduce((sum, point) => sum + point.percent, 0);
  return total >= 99;
}
